import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Edit2, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectExpenses = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [mgmtExpense, setMgmtExpense] = useState(false);
  const [editingName, setEditingName] = useState(null);

  useEffect(() => { fetchExpenses(); }, []);

  const fetchExpenses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/project-expenses`);
      const { data } = await readResponse(res);
      setExpenses(data.expenses || []);
    } catch { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Expense name is required'); return; }
    try {
      if (editingName) {
        await fetch(`${API_URL}/api/project-expenses/${encodeURIComponent(editingName)}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, management_expense: mgmtExpense })
        });
        toast.success('Expense updated');
      } else {
        const res = await fetch(`${API_URL}/api/project-expenses`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, management_expense: mgmtExpense })
        });
        if (!res.ok) { const { data: err } = await readResponse(res); toast.error(err.detail || 'Failed'); return; }
        toast.success('Expense created');
      }
      setName(''); setMgmtExpense(false); setEditingName(null);
      fetchExpenses();
    } catch { toast.error('Failed to save expense'); }
  };

  const handleEdit = (exp) => {
    setName(exp.name);
    setMgmtExpense(exp.management_expense);
    setEditingName(exp.name);
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Setup</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">Expense Setup</h1>
        </div>

        <div className="mb-6">
          <button onClick={() => navigate('/setup')} className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors" data-testid="back-btn">Back</button>
        </div>

        <div className="bg-white rounded shadow p-8">
          <div className="mb-8 space-y-2">
            <div className="flex items-start gap-2">
              <span className="bg-[#FFA94D] text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">Hint</span>
              <p className="text-gray-600 text-sm">Create expenses that your organization uses (examples - Home Depot, Sub-contract, Equipment Rental, Temp Labor). Expenses are selected in project expense drop down menu.</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="bg-[#FFA94D] text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">Hint</span>
              <p className="text-gray-600 text-sm">When implementing commission system, expenses commission personnel manage (labor, temp labor, vehicle fuel) are "Management Expenses". Resto MP then deducts those management expenses from commission amount due.</p>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left - Form */}
            <div className="w-full lg:w-72 flex-shrink-0">
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <label className="text-red-500 text-sm w-36 text-right">Expense:</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="expense-name-input" />
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-red-500 text-sm w-36 text-right">Management Expense:</label>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={mgmtExpense} onChange={(e) => setMgmtExpense(e.target.checked)} className="w-4 h-4" data-testid="mgmt-expense-checkbox" />
                    <HelpCircle className="w-5 h-5 text-[#5B8DB8]" />
                  </div>
                </div>
                <div className="flex justify-center mt-4">
                  <button onClick={handleSubmit} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded transition-colors" data-testid="update-data-btn">Update Data</button>
                </div>
              </div>
            </div>

            {/* Right - Table */}
            <div className="flex-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left p-3 text-gray-600 font-medium">Expense</th>
                    <th className="text-left p-3 text-gray-600 font-medium">Management Expense</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="3" className="p-4 text-center text-gray-500">Loading...</td></tr>
                  ) : expenses.length === 0 ? (
                    <tr><td colSpan="3" className="p-4 text-center text-gray-500 italic">No expenses found</td></tr>
                  ) : (
                    expenses.map((exp, idx) => (
                      <tr key={idx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="p-3 text-gray-700">{exp.name}</td>
                        <td className="p-3 text-center">
                          <input
                            type="checkbox"
                            checked={exp.management_expense}
                            onChange={async (e) => {
                              const newVal = e.target.checked;
                              try {
                                await fetch(`${API_URL}/api/project-expenses/${encodeURIComponent(exp.name)}`, {
                                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ management_expense: newVal })
                                });
                                setExpenses(prev => prev.map(ex => ex.name === exp.name ? { ...ex, management_expense: newVal } : ex));
                              } catch { toast.error('Failed to update'); }
                            }}
                            className="w-4 h-4 cursor-pointer"
                            data-testid={`mgmt-checkbox-${idx}`}
                          />
                        </td>
                        <td className="p-3">
                          <button onClick={() => handleEdit(exp)} className="border border-gray-300 rounded p-1 hover:bg-gray-100">
                            <Edit2 className="w-4 h-4 text-gray-500" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProjectExpenses;
